import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ChevronRight } from 'lucide-react';
import { SingleSelectDropdown } from './ui/SingleSelectDropdown';
import { MultiSelectDropdown } from './ui/MultiSelectDropdown';
import { saveSurveyResponse } from '../lib/supabase';
interface SurveyData {
  // Welcome
  email: string;
  // About You
  ageRange: string;
  genderIdentity: string;
  education: string;
  professionalBackground: string;
  professionalBackgroundOther: string;
  primaryLocation: string;
  // Your Company
  companyDuration: string;
  fundingStage: string;
  industry: string;
  industryOther: string;
  teamSize: string;
  workingFullTime: string;
  workingFullTimeOther: string;
  // Why Solo
  decisionInfluence: string;
  cofounderConsideration: string;
  futureCofounder: string;
  // Confidence & Investor Perception
  confidenceRating: number;
  investorPerception: string;
  investorReadiness: number;
  investorResponse: string;
  // Challenges & Dynamics
  challengesExperienced: string[];
  challengesExperiencedOther: string;
  biggestChallenge: string[];
  biggestChallengeOther: string;
  // Time & Task Management
  tasksSpentOn: string[];
  tasksSpentOnOther: string;
  timeManagementEffectiveness: number;
  internalSystems: string[];
  delegatedAreas: string[];
  // Isolation & Stress
  isolationFrequency: string;
  stressFrequency: string;
  scheduledCheckIns: string;
  founderCommunities: string;
  friendsFamily: string;
  hobbiesTime: string;
  // Work-Life Balance & Burnout
  healthyWorkLife: string;
  burnoutRating: number;
  resilienceRating: number;
  workLifeBalanceRating: string;
  decisionMakingRating: string;
  lonelinessRating: string;
  progressRating: string;
  // Support Systems
  supportSystems: string[];
  supportSystemsOther: string;
  feedbackSource: string;
  feedbackSourceOther: string;
  // Decision-Making
  hardestAreaToHandOver: string;
  cofounderMoment: string;
  // Personal Development
  personalDevelopmentFrequency: string;
  selfDisciplineRanking: number;
  emotionalResilienceRanking: number;
  adaptabilityRanking: number;
  clearVisionRanking: number;
  financialPrudenceRanking: number;
  // Mindset & Beliefs
  autonomyOutweighsChallenges: string;
  canSucceedWithoutCofounder: string;
  fasterDecisionMaking: string;
  // Resources & Support
  booksPublications: string;
  podcastsInterviews: string;
  formalMentorship: string;
  industryCommunities: string;
  onlineCourses: string;
  peerGroups: string;
  executiveCoaching: string;
  therapySupport: string;
  // Future & Success
  personalDefinitionOfSuccess: string;
  useAssessmentTool: string;
  // Final Thoughts
  anythingElse: string;
  openToFollowUp: string;
  confirmEmail: string;
}
const SECTIONS = [{
  id: 'welcome',
  label: 'Welcome',
  required: false
}, {
  id: 'about',
  label: 'About You',
  required: true
}, {
  id: 'company',
  label: 'Your Company',
  required: true
}, {
  id: 'why-solo',
  label: 'Why Solo',
  required: true
}, {
  id: 'confidence',
  label: 'Confidence',
  required: false
}, {
  id: 'challenges',
  label: 'Challenges',
  required: false
}, {
  id: 'time',
  label: 'Time Management',
  required: false
}, {
  id: 'isolation',
  label: 'Isolation & Stress',
  required: false
}, {
  id: 'balance',
  label: 'Work-Life Balance',
  required: false
}, {
  id: 'support',
  label: 'Support Systems',
  required: false
}, {
  id: 'decisions',
  label: 'Decision-Making',
  required: false
}, {
  id: 'development',
  label: 'Personal Development',
  required: false
}, {
  id: 'mindset',
  label: 'Mindset',
  required: false
}, {
  id: 'resources',
  label: 'Resources',
  required: false
}, {
  id: 'future',
  label: 'Future & Success',
  required: false
}, {
  id: 'final',
  label: 'Final Thoughts',
  required: false
}];
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzg6bwCBFRFEzM_QmklQ86wdGOVbrDY4z8NXQakQknBzF0vvaCIaj7jBtZMK_X3rjfy/exec';
export function SoloFounderSurvey({
  onClose
}: {
  onClose: () => void;
}) {
  const [currentSection, setCurrentSection] = useState('welcome');
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState<SurveyData>({
    email: '',
    ageRange: '',
    genderIdentity: '',
    education: '',
    professionalBackground: '',
    professionalBackgroundOther: '',
    primaryLocation: '',
    companyDuration: '',
    fundingStage: '',
    industry: '',
    industryOther: '',
    teamSize: '',
    workingFullTime: '',
    workingFullTimeOther: '',
    decisionInfluence: '',
    cofounderConsideration: '',
    futureCofounder: '',
    confidenceRating: 0,
    investorPerception: '',
    investorReadiness: 0,
    investorResponse: '',
    challengesExperienced: [],
    challengesExperiencedOther: '',
    biggestChallenge: [],
    biggestChallengeOther: '',
    tasksSpentOn: [],
    tasksSpentOnOther: '',
    timeManagementEffectiveness: 0,
    internalSystems: [],
    delegatedAreas: [],
    isolationFrequency: '',
    stressFrequency: '',
    scheduledCheckIns: '',
    founderCommunities: '',
    friendsFamily: '',
    hobbiesTime: '',
    healthyWorkLife: '',
    burnoutRating: 0,
    resilienceRating: 0,
    workLifeBalanceRating: '',
    decisionMakingRating: '',
    lonelinessRating: '',
    progressRating: '',
    supportSystems: [],
    supportSystemsOther: '',
    feedbackSource: '',
    feedbackSourceOther: '',
    hardestAreaToHandOver: '',
    cofounderMoment: '',
    personalDevelopmentFrequency: '',
    selfDisciplineRanking: 0,
    emotionalResilienceRanking: 0,
    adaptabilityRanking: 0,
    clearVisionRanking: 0,
    financialPrudenceRanking: 0,
    autonomyOutweighsChallenges: '',
    canSucceedWithoutCofounder: '',
    fasterDecisionMaking: '',
    booksPublications: '',
    podcastsInterviews: '',
    formalMentorship: '',
    industryCommunities: '',
    onlineCourses: '',
    peerGroups: '',
    executiveCoaching: '',
    therapySupport: '',
    personalDefinitionOfSuccess: '',
    useAssessmentTool: '',
    anythingElse: '',
    openToFollowUp: '',
    confirmEmail: ''
  });
  const [sessionId] = useState(() => `solo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [isSaving, setIsSaving] = useState(false);
  const toggleArrayItem = (array: string[], item: string) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    }
    return [...array, item];
  };
  const autoSaveProgress = async () => {
    setIsSaving(true);
    try {
      const metadata = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referrer: document.referrer || 'Direct'
      };
      const submissionData = {
        ...formData,
        sessionId,
        completedSections: Array.from(completedSections).join(', '),
        currentSection,
        isComplete: false,
        metadata
      };
      // Save to Google Sheets
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });
      // Save to Supabase
      await saveSurveyResponse('solo_founder_responses', {
        sessionId,
        surveyData: formData,
        isComplete: false,
        completedSections: Array.from(completedSections).join(', '),
        currentSection,
        metadata
      });
      console.log('✅ Progress auto-saved');
    } catch (error) {
      console.error('❌ Auto-save error:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const markSectionComplete = (sectionId: string) => {
    setCompletedSections(prev => new Set([...prev, sectionId]));
  };
  const goToNextSection = async () => {
    markSectionComplete(currentSection);
    // Auto-save progress
    await autoSaveProgress();
    const currentIndex = SECTIONS.findIndex(s => s.id === currentSection);
    if (currentIndex < SECTIONS.length - 1) {
      setCurrentSection(SECTIONS[currentIndex + 1].id);
      window.scrollTo(0, 0);
    }
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const metadata = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referrer: document.referrer || 'Direct'
      };
      const submissionData = {
        ...formData,
        sessionId,
        completedSections: Array.from(completedSections).join(', '),
        currentSection: 'final',
        isComplete: true,
        metadata
      };
      console.log('🚀 Submitting to:', GOOGLE_SCRIPT_URL);
      console.log('📝 Form data with metadata:', JSON.stringify(submissionData, null, 2));
      // Save to Google Sheets
      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });
      // Save to Supabase
      await saveSurveyResponse('solo_founder_responses', {
        sessionId,
        surveyData: formData,
        isComplete: true,
        completedSections: Array.from(completedSections).join(', '),
        currentSection: 'final',
        metadata
      });
      console.log('✅ Submission successful!');
      setIsSubmitted(true);
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    } catch (error) {
      console.error('❌ Submission error:', error);
      setSubmitError('There was an error submitting your response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted) {
    return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-1182">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-white rounded-[16px] p-12 max-w-md w-full text-center" data-id="element-1183">
          <div className="w-16 h-16 bg-[#C5F467] rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1184">
            <Check className="w-8 h-8 text-[#1A3D3A]" data-id="element-1185" />
          </div>
          <h2 className="text-[#1A3D3A] mb-4" style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: '32px',
          fontWeight: 500
        }} data-id="element-1186">
            Thank You!
          </h2>
          <p className="text-[#3D5654] mb-8" style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '16px',
          lineHeight: 1.6
        }} data-id="element-1187">
            Your insights are invaluable to understanding the solo founder
            experience and improving support for entrepreneurs.
          </p>
          <p className="text-[#3D5654] text-sm" style={{
          fontFamily: "'DM Sans', sans-serif",
          opacity: 0.7
        }} data-id="element-1188">
            Redirecting you to the home page...
          </p>
        </motion.div>
      </div>;
  }
  const renderSection = () => {
    switch (currentSection) {
      case 'welcome':
        return <div className="space-y-8" data-id="element-1189">
            <div data-id="element-1190">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1191">
                Email Address (optional)
              </label>
              <input type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="your@email.com" data-id="element-1192" />
            </div>

            <div className="bg-[#F8FAF9] p-6 rounded-lg" data-id="element-1193">
              <p className="text-[#3D5654]" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              lineHeight: 1.7
            }} data-id="element-1194">
                Thank you for participating in this research on the solo founder
                experience. Your insights will help us better understand the
                unique challenges, strengths, and support needs of founders
                building companies on their own. The survey takes approximately
                12-15 minutes to complete.
              </p>
            </div>
          </div>;
      case 'about':
        return <div className="space-y-8" data-id="element-1195">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1196">
              About You
            </h3>

            <SingleSelectDropdown label="What is your age range?" options={['18–24', '25–34', '35–44', '45–54', '55–64', '65+', 'Prefer not to say']} selected={formData.ageRange} onChange={selected => setFormData({
            ...formData,
            ageRange: selected
          })} placeholder="Select age range..." data-id="element-1197" />

            <SingleSelectDropdown label="What is your gender identity?" options={['Male', 'Female', 'Non-binary', 'Other', 'Prefer not to say']} selected={formData.genderIdentity} onChange={selected => setFormData({
            ...formData,
            genderIdentity: selected
          })} placeholder="Select gender identity..." data-id="element-1198" />

            <SingleSelectDropdown label="What is your highest level of education?" options={['High school or equivalent', 'Some college', "Bachelor's degree", "Master's degree", 'MBA', 'PhD or Doctorate', 'Other professional degree', 'Self-taught or No formal degree']} selected={formData.education} onChange={selected => setFormData({
            ...formData,
            education: selected
          })} placeholder="Select education level..." data-id="element-1199" />

            <SingleSelectDropdown label="What is your primary professional background?" options={['Technical (Engineering or Development)', 'Product', 'Design', 'Sales or Business Development', 'Marketing', 'Finance', 'Operations', 'Legal', 'Industry or Domain Expert', 'Generalist', 'Other']} selected={formData.professionalBackground} onChange={selected => setFormData({
            ...formData,
            professionalBackground: selected
          })} placeholder="Select background..." otherValue={formData.professionalBackgroundOther} onOtherChange={value => setFormData({
            ...formData,
            professionalBackgroundOther: value
          })} data-id="element-1200" />

            <SingleSelectDropdown label="Where are you primarily based?" options={['North America', 'Europe', 'Asia-Pacific', 'Latin America', 'Middle East & Africa', 'Fully remote or No fixed location']} selected={formData.primaryLocation} onChange={selected => setFormData({
            ...formData,
            primaryLocation: selected
          })} placeholder="Select location..." data-id="element-1201" />
          </div>;
      case 'company':
        return <div className="space-y-8" data-id="element-1202">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1203">
              Your Company
            </h3>

            <SingleSelectDropdown label="How long have you been a solo founder of your company?" options={['Less than 1 year', '1–2 years', '3–5 years', '6–10 years', 'More than 10 years']} selected={formData.companyDuration} onChange={selected => setFormData({
            ...formData,
            companyDuration: selected
          })} placeholder="Select duration..." data-id="element-1204" />

            <SingleSelectDropdown label="What funding stage is your company currently at?" options={['Pre-Seed (concept or idea stage)', 'Seed (MVP or early traction)', 'Series A (scaling product and team)', 'Series B–C (growth stage)', 'Series D+ or Exit stage', 'Bootstrapped (not seeking venture funding)']} selected={formData.fundingStage} onChange={selected => setFormData({
            ...formData,
            fundingStage: selected
          })} placeholder="Select funding stage..." data-id="element-1205" />

            <SingleSelectDropdown label="What industry is your company in?" options={['SaaS or Software', 'Consumer Tech', 'Fintech', 'Healthcare or Biotech', 'E-commerce or Retail', 'Climate or Cleantech', 'EdTech', 'AI or ML', 'Hardware', 'Media or Entertainment', 'Professional Services', 'Marketplace', 'Other']} selected={formData.industry} onChange={selected => setFormData({
            ...formData,
            industry: selected
          })} placeholder="Select industry..." otherValue={formData.industryOther} onOtherChange={value => setFormData({
            ...formData,
            industryOther: value
          })} data-id="element-1206" />

            <SingleSelectDropdown label="What is your current team size (excluding yourself)?" options={['Just me (no employees)', '1–5 employees', '6–15 employees', '16–50 employees', '51–100 employees', 'More than 100 employees']} selected={formData.teamSize} onChange={selected => setFormData({
            ...formData,
            teamSize: selected
          })} placeholder="Select team size..." data-id="element-1207" />

            <SingleSelectDropdown label="Are you working on your company full-time?" options={['Yes, full-time', 'No, part-time while employed elsewhere', 'No, part-time while freelancing', 'No, part-time while studying', 'Other']} selected={formData.workingFullTime} onChange={selected => setFormData({
            ...formData,
            workingFullTime: selected
          })} placeholder="Select work status..." otherValue={formData.workingFullTimeOther} onOtherChange={value => setFormData({
            ...formData,
            workingFullTimeOther: value
          })} data-id="element-1208" />
          </div>;
      case 'why-solo':
        return <div className="space-y-8" data-id="element-1209">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1210">
              Why Solo
            </h3>

            <div data-id="element-1211">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1212">
                What influenced your decision to build your company as a solo
                founder?
              </label>
              <textarea value={formData.decisionInfluence} onChange={e => setFormData({
              ...formData,
              decisionInfluence: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your thoughts..." data-id="element-1213" />
            </div>

            <SingleSelectDropdown label="Have you considered bringing on a cofounder?" options={['I have never considered bringing on a cofounder', 'I have considered it but decided to stay solo', 'I am open to adding a cofounder in the future', 'I prefer to remain a solo founder', "I'm unsure or undecided", 'I started with a cofounder but continued the journey solo']} selected={formData.cofounderConsideration} onChange={selected => setFormData({
            ...formData,
            cofounderConsideration: selected
          })} placeholder="Select answer..." data-id="element-1214" />

            <SingleSelectDropdown label="Would you consider bringing on a cofounder in the future?" options={['Yes, definitely', 'Maybe, under the right circumstances', 'No, I prefer to remain solo']} selected={formData.futureCofounder} onChange={selected => setFormData({
            ...formData,
            futureCofounder: selected
          })} placeholder="Select answer..." data-id="element-1215" />
          </div>;
      case 'confidence':
        return <div className="space-y-8" data-id="element-1216">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1217">
              Confidence & Investor Perception
            </h3>

            <div data-id="element-1218">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1219">
                On a scale of 1–5, how confident are you in your ability to
                succeed solo?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-1220">
                1 = Not confident at all → 5 = Extremely confident
              </p>
              <div className="flex gap-4 justify-center" data-id="element-1221">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                confidenceRating: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.confidenceRating === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-1222">
                    {num}
                  </button>)}
              </div>
            </div>

            <div data-id="element-1223">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1224">
                Some investors view solo founders as higher risk. Do you feel
                this perception affects you, and if so, how do you address it?
              </label>
              <textarea value={formData.investorPerception} onChange={e => setFormData({
              ...formData,
              investorPerception: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your experience..." data-id="element-1225" />
            </div>

            <div data-id="element-1226">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1227">
                On a scale of 1–5, how equipped do you feel to meet investor
                expectations solo?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-1228">
                1 = Not equipped at all → 5 = Fully equipped
              </p>
              <div className="flex gap-4 justify-center" data-id="element-1229">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                investorReadiness: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.investorReadiness === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-1230">
                    {num}
                  </button>)}
              </div>
            </div>

            <div data-id="element-1231">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1232">
                Imagine an investor told you they would only back you if you
                added a cofounder. How would you respond?
              </label>
              <textarea value={formData.investorResponse} onChange={e => setFormData({
              ...formData,
              investorResponse: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your response..." data-id="element-1233" />
            </div>
          </div>;
      case 'challenges':
        return <div className="space-y-8" data-id="element-1234">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1235">
              Challenges & Dynamics
            </h3>

            <MultiSelectDropdown label="Which of the following challenges have you experienced as a solo founder? (Select all that apply)" options={['Making major decisions without a sounding board', 'Managing self-doubt or internal pressure', 'Carrying the emotional weight of the company alone', 'Balancing urgency with personal wellbeing', 'Wearing too many roles without enough support', 'Financial strain or personal financial risk', 'Sustaining motivation during slow or uncertain phases', 'Difficulty building a trusted advisory or support circle', 'Feeling isolated at key inflection points', 'Decision fatigue', 'Other']} selected={formData.challengesExperienced} onChange={selected => setFormData({
            ...formData,
            challengesExperienced: selected
          })} placeholder="Select challenges..." otherValue={formData.challengesExperiencedOther} onOtherChange={value => setFormData({
            ...formData,
            challengesExperiencedOther: value
          })} data-id="element-1236" />

            <MultiSelectDropdown label="What's your biggest challenge running your business? (Select all that apply)" options={['Making key decisions without a thought partner', 'Staying motivated and accountable over time', 'Managing the emotional load of building alone', 'Balancing vision and execution without enough support', 'Financial or fundraising pressure', 'Lacking certain skills or expertise', 'Feeling isolated or without a true peer group', 'Other']} selected={formData.biggestChallenge} onChange={selected => setFormData({
            ...formData,
            biggestChallenge: selected
          })} placeholder="Select challenges..." otherValue={formData.biggestChallengeOther} onOtherChange={value => setFormData({
            ...formData,
            biggestChallengeOther: value
          })} data-id="element-1237" />
          </div>;
      case 'time':
        return <div className="space-y-8" data-id="element-1238">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1239">
              Time & Task Management
            </h3>

            <MultiSelectDropdown label="Which of the following tasks do you spend the most time on? (Select up to 3)" options={['Product Development or Coding', 'Sales or Business Development', 'Marketing or Content Creation', 'Customer Support or Success', 'Operations or Administrative Tasks', 'Financial Management or Fundraising', 'Strategic Planning or Vision', 'Other']} selected={formData.tasksSpentOn} onChange={selected => {
            if (selected.length <= 3) {
              setFormData({
                ...formData,
                tasksSpentOn: selected
              });
            }
          }} placeholder="Select up to 3 tasks..." otherValue={formData.tasksSpentOnOther} onOtherChange={value => setFormData({
            ...formData,
            tasksSpentOnOther: value
          })} data-id="element-1240" />

            <div data-id="element-1241">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1242">
                On a scale of 1–5, how effectively do you manage your time and
                prioritize tasks as a solo founder?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-1243">
                1 = Very Ineffective → 5 = Highly Effective
              </p>
              <div className="flex gap-4 justify-center" data-id="element-1244">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                timeManagementEffectiveness: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.timeManagementEffectiveness === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-1245">
                    {num}
                  </button>)}
              </div>
            </div>

            <MultiSelectDropdown label="Which of the following internal systems or routines do you use to manage your operational load? (Select all that apply)" options={['Time blocking or Advanced scheduling', 'Weekly or Daily goal setting and review', 'Delegation to VAs or Contractors or Tools', 'Strict boundary setting for work hours', 'Regular physical exercise or meditation', 'None of the above']} selected={formData.internalSystems} onChange={selected => setFormData({
            ...formData,
            internalSystems: selected
          })} placeholder="Select systems..." data-id="element-1246" />

            <MultiSelectDropdown label="Which key operational areas, if any, do you currently delegate or outsource? (Select all that apply)" options={['Financial or Accounting', 'Marketing or Social Media Management', 'Technical Development or Coding', 'Customer Service', 'Administrative or Virtual Assistant tasks', 'Legal or HR', 'None of the above']} selected={formData.delegatedAreas} onChange={selected => setFormData({
            ...formData,
            delegatedAreas: selected
          })} placeholder="Select areas..." data-id="element-1247" />
          </div>;
      case 'isolation':
        return <div className="space-y-8" data-id="element-1248">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1249">
              Isolation & Stress
            </h3>

            <SingleSelectDropdown label="Being a solo founder can feel isolating at times — how often do you feel that way?" options={['Rarely', 'Sometimes', 'Often', 'Constantly', 'Varies']} selected={formData.isolationFrequency} onChange={selected => setFormData({
            ...formData,
            isolationFrequency: selected
          })} placeholder="Select frequency..." data-id="element-1250" />

            <SingleSelectDropdown label="How often do you feel stressed as a solo founder?" options={['Rarely', 'Sometimes', 'Often', 'Constantly', 'Varies']} selected={formData.stressFrequency} onChange={selected => setFormData({
            ...formData,
            stressFrequency: selected
          })} placeholder="Select frequency..." data-id="element-1251" />

            <div data-id="element-1252">
              <label className="block text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1253">
                Please rate the level of effectiveness of the following
                strategies in combating feelings of isolation:
              </label>
              <div className="space-y-4" data-id="element-1254">
                {[{
                key: 'scheduledCheckIns',
                label: 'Scheduled check-ins with peers or mentors'
              }, {
                key: 'founderCommunities',
                label: 'Participating in founder communities or groups'
              }, {
                key: 'friendsFamily',
                label: 'Regular interactions with friends and family'
              }, {
                key: 'hobbiesTime',
                label: 'Dedicated time for hobbies or personal interests'
              }].map(({
                key,
                label
              }) => <div key={key} data-id="element-1255">
                    <p className="text-[#3D5654] mb-2" style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '14px'
                }} data-id="element-1256">
                      {label}
                    </p>
                    <div className="flex gap-2" data-id="element-1257">
                      {['Not Effective', 'Slightly Effective', 'Moderately Effective', 'Very Effective'].map(rating => <button key={rating} onClick={() => setFormData({
                    ...formData,
                    [key]: rating
                  })} className={`flex-1 py-2 rounded-lg border-2 transition-all text-sm ${formData[key as keyof SurveyData] === rating ? 'bg-[#1A3D3A] text-white border-[#1A3D3A]' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40'}`} style={{
                    fontFamily: "'DM Sans', sans-serif"
                  }} data-id="element-1258">
                          {rating}
                        </button>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>;
      case 'balance':
        return <div className="space-y-8" data-id="element-1259">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1260">
              Work-Life Balance & Burnout
            </h3>

            <SingleSelectDropdown label="Do you feel you have a healthy work-life balance?" options={['Yes', 'No', 'Sometimes']} selected={formData.healthyWorkLife} onChange={selected => setFormData({
            ...formData,
            healthyWorkLife: selected
          })} placeholder="Select answer..." data-id="element-1261" />

            <div data-id="element-1262">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1263">
                On a scale of 1–5, how close have you come to burnout?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-1264">
                1 = Not close at all → 5 = Completely burned out
              </p>
              <div className="flex gap-4 justify-center" data-id="element-1265">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                burnoutRating: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.burnoutRating === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-1266">
                    {num}
                  </button>)}
              </div>
            </div>

            <div data-id="element-1267">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1268">
                On a scale of 1–5, how resilient do you feel right now?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-1269">
                1 = Not resilient at all → 5 = Extremely resilient
              </p>
              <div className="flex gap-4 justify-center" data-id="element-1270">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                resilienceRating: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.resilienceRating === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-1271">
                    {num}
                  </button>)}
              </div>
            </div>

            <div data-id="element-1272">
              <label className="block text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1273">
                How would you rate the following aspects of being a solo
                founder?
              </label>
              <div className="space-y-4" data-id="element-1274">
                {[{
                key: 'workLifeBalanceRating',
                label: 'Work-Life Balance'
              }, {
                key: 'decisionMakingRating',
                label: 'Decision-Making'
              }, {
                key: 'lonelinessRating',
                label: 'Feeling of Loneliness'
              }, {
                key: 'progressRating',
                label: 'Pace of Progress'
              }].map(({
                key,
                label
              }) => <div key={key} data-id="element-1275">
                    <p className="text-[#3D5654] mb-2" style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '14px'
                }} data-id="element-1276">
                      {label}
                    </p>
                    <div className="flex gap-2" data-id="element-1277">
                      {['Poor', 'Fair', 'Good', 'Excellent'].map(rating => <button key={rating} onClick={() => setFormData({
                    ...formData,
                    [key]: rating
                  })} className={`flex-1 py-2 rounded-lg border-2 transition-all text-sm ${formData[key as keyof SurveyData] === rating ? 'bg-[#1A3D3A] text-white border-[#1A3D3A]' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40'}`} style={{
                    fontFamily: "'DM Sans', sans-serif"
                  }} data-id="element-1278">
                          {rating}
                        </button>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>;
      case 'support':
        return <div className="space-y-8" data-id="element-1279">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1280">
              Support Systems
            </h3>

            <MultiSelectDropdown label="Which support systems do you rely on most? (Select all that apply)" options={['Advisors', 'Therapy or Coaching', 'Peer groups', 'Friends or Family', 'Other']} selected={formData.supportSystems} onChange={selected => setFormData({
            ...formData,
            supportSystems: selected
          })} placeholder="Select support systems..." otherValue={formData.supportSystemsOther} onOtherChange={value => setFormData({
            ...formData,
            supportSystemsOther: value
          })} data-id="element-1281" />

            <SingleSelectDropdown label="When you face tough calls, who do you usually turn to for feedback?" options={['A close friend or trusted peer', 'Family members', 'Former colleagues or industry peers', 'Professional advisors or mentors', 'An executive coach or therapist', 'Online communities or founder groups', 'I usually make decisions alone', 'Other']} selected={formData.feedbackSource} onChange={selected => setFormData({
            ...formData,
            feedbackSource: selected
          })} placeholder="Select answer..." otherValue={formData.feedbackSourceOther} onOtherChange={value => setFormData({
            ...formData,
            feedbackSourceOther: value
          })} data-id="element-1282" />
          </div>;
      case 'decisions':
        return <div className="space-y-8" data-id="element-1283">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1284">
              Decision-Making
            </h3>

            <div data-id="element-1285">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1286">
                Imagine if you had to share decision-making power tomorrow.
                Which area would be the hardest for you to hand over?
              </label>
              <textarea value={formData.hardestAreaToHandOver} onChange={e => setFormData({
              ...formData,
              hardestAreaToHandOver: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your thoughts..." data-id="element-1287" />
            </div>

            <div data-id="element-1288">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1289">
                Can you share a moment where you really wished you had a
                cofounder? What happened, and how did you manage it?
              </label>
              <textarea value={formData.cofounderMoment} onChange={e => setFormData({
              ...formData,
              cofounderMoment: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your experience..." data-id="element-1290" />
            </div>
          </div>;
      case 'development':
        return <div className="space-y-8" data-id="element-1291">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1292">
              Personal Development
            </h3>

            <SingleSelectDropdown label="How often do you dedicate focused time to personal or professional development (e.g., learning new skills, self-reflection)?" options={['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Rarely or Never']} selected={formData.personalDevelopmentFrequency} onChange={selected => setFormData({
            ...formData,
            personalDevelopmentFrequency: selected
          })} placeholder="Select frequency..." data-id="element-1293" />

            <div data-id="element-1294">
              <label className="block text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1295">
                Rank the following personal traits by how critical they are for
                a solo founder's long-term success (1 being most critical):
              </label>
              <div className="space-y-4" data-id="element-1296">
                {[{
                key: 'selfDisciplineRanking',
                label: 'Self-Discipline'
              }, {
                key: 'emotionalResilienceRanking',
                label: 'Emotional Resilience'
              }, {
                key: 'adaptabilityRanking',
                label: 'Adaptability or Flexibility'
              }, {
                key: 'clearVisionRanking',
                label: 'Clear Vision or Purpose'
              }, {
                key: 'financialPrudenceRanking',
                label: 'Financial Prudence'
              }].map(({
                key,
                label
              }) => <div key={key} className="flex items-center gap-4" data-id="element-1297">
                    <span className="flex-1 text-[#3D5654]" style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '14px'
                }} data-id="element-1298">
                      {label}
                    </span>
                    <div className="flex gap-2" data-id="element-1299">
                      {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                    ...formData,
                    [key]: num
                  })} className={`w-10 h-10 rounded-lg border-2 transition-all ${formData[key as keyof SurveyData] === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A]' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40'}`} style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '14px',
                    fontWeight: 600
                  }} data-id="element-1300">
                          {num}
                        </button>)}
                    </div>
                  </div>)}
              </div>
              <p className="text-[#3D5654] text-sm mt-2" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-1301">
                1 = Most Critical → 5 = Least Critical
              </p>
            </div>
          </div>;
      case 'mindset':
        return <div className="space-y-8" data-id="element-1302">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1303">
              Mindset & Beliefs
            </h3>

            <div data-id="element-1304">
              <label className="block text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1305">
                Please rate your agreement with the following statements:
              </label>
              <div className="space-y-4" data-id="element-1306">
                {[{
                key: 'autonomyOutweighsChallenges',
                label: 'The complete autonomy of being a solo founder outweighs the challenges'
              }, {
                key: 'canSucceedWithoutCofounder',
                label: 'I believe my company can achieve significant success without a cofounder'
              }, {
                key: 'fasterDecisionMaking',
                label: 'My pace of decision-making is faster because I am solo'
              }].map(({
                key,
                label
              }) => <div key={key} data-id="element-1307">
                    <p className="text-[#3D5654] mb-2" style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '14px'
                }} data-id="element-1308">
                      {label}
                    </p>
                    <div className="flex gap-2" data-id="element-1309">
                      {['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'].map(rating => <button key={rating} onClick={() => setFormData({
                    ...formData,
                    [key]: rating
                  })} className={`flex-1 py-2 rounded-lg border-2 transition-all text-xs ${formData[key as keyof SurveyData] === rating ? 'bg-[#1A3D3A] text-white border-[#1A3D3A]' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40'}`} style={{
                    fontFamily: "'DM Sans', sans-serif"
                  }} data-id="element-1310">
                          {rating}
                        </button>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>;
      case 'resources':
        return <div className="space-y-8" data-id="element-1311">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1312">
              Resources & Support
            </h3>

            <div data-id="element-1313">
              <label className="block text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1314">
                How would you rate the usefulness of the following resources for
                the solo founder journey?
              </label>
              <div className="space-y-4" data-id="element-1315">
                {[{
                key: 'booksPublications',
                label: 'Books or publications'
              }, {
                key: 'podcastsInterviews',
                label: 'Podcasts or Interviews with founders'
              }, {
                key: 'formalMentorship',
                label: 'Formal mentorship programs'
              }, {
                key: 'industryCommunities',
                label: 'Industry-specific communities or networks'
              }, {
                key: 'onlineCourses',
                label: 'Online courses or educational content'
              }, {
                key: 'peerGroups',
                label: 'Peer founder groups or masterminds'
              }, {
                key: 'executiveCoaching',
                label: 'Executive coaching'
              }, {
                key: 'therapySupport',
                label: 'Therapy or mental health support'
              }].map(({
                key,
                label
              }) => <div key={key} data-id="element-1316">
                    <p className="text-[#3D5654] mb-2" style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '14px'
                }} data-id="element-1317">
                      {label}
                    </p>
                    <div className="flex gap-2" data-id="element-1318">
                      {['Not Useful', 'Slightly Useful', 'Moderately Useful', 'Very Useful'].map(rating => <button key={rating} onClick={() => setFormData({
                    ...formData,
                    [key]: rating
                  })} className={`flex-1 py-2 rounded-lg border-2 transition-all text-sm ${formData[key as keyof SurveyData] === rating ? 'bg-[#1A3D3A] text-white border-[#1A3D3A]' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40'}`} style={{
                    fontFamily: "'DM Sans', sans-serif"
                  }} data-id="element-1319">
                          {rating}
                        </button>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>;
      case 'future':
        return <div className="space-y-8" data-id="element-1320">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1321">
              Future & Success
            </h3>

            <div data-id="element-1322">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1323">
                How would you describe your personal definition of success for
                this company or project?
              </label>
              <textarea value={formData.personalDefinitionOfSuccess} onChange={e => setFormData({
              ...formData,
              personalDefinitionOfSuccess: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your definition of success..." data-id="element-1324" />
            </div>

            <SingleSelectDropdown label="If you had access to a structured assessment or tool to support solo founders, would you use it?" options={['Yes', 'No', 'Maybe']} selected={formData.useAssessmentTool} onChange={selected => setFormData({
            ...formData,
            useAssessmentTool: selected
          })} placeholder="Select answer..." data-id="element-1325" />
          </div>;
      case 'final':
        return <div className="space-y-8" data-id="element-1326">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-1327">
              Final Thoughts
            </h3>

            <div data-id="element-1328">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1329">
                Is there anything else you would like to share about your
                experience as a solo founder?
              </label>
              <textarea value={formData.anythingElse} onChange={e => setFormData({
              ...formData,
              anythingElse: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share any additional thoughts..." data-id="element-1330" />
            </div>

            <SingleSelectDropdown label="Would you be open to a follow-up conversation to discuss your experiences in more depth?" options={['Yes', 'No', 'Maybe']} selected={formData.openToFollowUp} onChange={selected => setFormData({
            ...formData,
            openToFollowUp: selected
          })} placeholder="Select answer..." data-id="element-1331" />

            {formData.openToFollowUp === 'Yes' && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} data-id="element-1332">
                <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-1333">
                  If yes, please confirm your email (optional)
                </label>
                <input type="email" value={formData.confirmEmail} onChange={e => setFormData({
              ...formData,
              confirmEmail: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="your@email.com" data-id="element-1334" />
              </motion.div>}

            <div className="bg-[#F8FAF9] p-6 rounded-lg" data-id="element-1335">
              <p className="text-[#3D5654]" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              lineHeight: 1.7
            }} data-id="element-1336">
                Thank you for completing this survey! Your insights are
                invaluable to understanding the solo founder experience.
              </p>
            </div>
          </div>;
      default:
        return <div data-id="element-1337">Section coming soon...</div>;
    }
  };
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto" data-id="element-1338">
      <div className="min-h-screen flex items-start justify-center p-4 py-8" data-id="element-1339">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-[16px] w-full max-w-4xl relative" data-id="element-1340">
          {/* Header */}
          <div className="p-6 border-b border-[#1A3D3A]/10" data-id="element-1341">
            <div className="flex justify-between items-start mb-4" data-id="element-1342">
              <div data-id="element-1343">
                <h2 className="text-[#1A3D3A] mb-2" style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '28px',
                fontWeight: 500
              }} data-id="element-1344">
                  Solo Founder Research
                </h2>
                <p className="text-[#3D5654] text-sm" style={{
                fontFamily: "'DM Sans', sans-serif"
              }} data-id="element-1345">
                  {completedSections.size} of {SECTIONS.length} sections
                  completed
                </p>
              </div>
              <button onClick={onClose} className="text-[#3D5654] hover:text-[#1A3D3A] transition-colors" data-id="element-1346">
                <X className="w-6 h-6" data-id="element-1347" />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-1348">
              {SECTIONS.map(section => <button key={section.id} onClick={() => setCurrentSection(section.id)} className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm transition-all flex items-center gap-2 ${currentSection === section.id ? 'bg-[#1A3D3A] text-white' : completedSections.has(section.id) ? 'bg-[#C5F467] text-[#1A3D3A]' : 'bg-[#F8FAF9] text-[#3D5654] hover:bg-[#E8EAE9]'}`} style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 500
            }} data-id="element-1349">
                  {completedSections.has(section.id) && <Check className="w-4 h-4" data-id="element-1350" />}
                  {section.label}
                </button>)}
            </div>
          </div>

          {/* Content */}
          <div className="p-8" data-id="element-1351">
            <AnimatePresence mode="wait" data-id="element-1352">
              <motion.div key={currentSection} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.2
            }} data-id="element-1353">
                {renderSection()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Footer */}
          <div className="p-6 border-t border-[#1A3D3A]/10 flex justify-between items-center" data-id="element-1354">
            <div className="text-sm text-[#3D5654]" style={{
            fontFamily: "'DM Sans', sans-serif"
          }} data-id="element-1355">
              {SECTIONS.find(s => s.id === currentSection)?.required && <span data-id="element-1356">* Required section</span>}
              {isSaving && <span className="text-[#C5F467] ml-4" data-id="element-1357">💾 Saving...</span>}
            </div>
            <button onClick={currentSection === 'final' ? handleSubmit : goToNextSection} disabled={isSubmitting || isSaving} className="flex items-center gap-2 px-8 py-3 bg-[#1A3D3A] text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50" style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '14px',
            fontWeight: 600
          }} data-id="element-1358">
              {isSubmitting ? 'Submitting...' : isSaving ? 'Saving...' : currentSection === 'final' ? 'Submit Survey' : <>
                  Continue
                  <ChevronRight className="w-4 h-4" data-id="element-1359" />
                </>}
            </button>
          </div>
        </motion.div>
      </div>
    </div>;
}